// @ts-check
import { urlFriendlyParam } from '@bto-services/utils';
import { ITEM_TYPES, BETSLIP_LAYOUTS, BETSLIP_TYPES, ASSISTED_BET_OPTIONS, BETSLIP_MSG_STATUS, PLACE_STATUS_TO_QUICKBET_CLASS } from './const';
import { RACE_MARKET_ID_NAME } from 'bto-models/match/races/const';
import Vue from 'vue';


/**
 * @typedef {import('./interfaces').BetslipItem} BetslipItem
 * @typedef {import('./interfaces').BetBuilderFixture} BetBuilderFixture
 * @typedef {import('./interfaces').BetslipMatchItem} BetslipMatchItem
 * @typedef {import('./interfaces').BetslipStore} BetslipStore
 */

const getMatchId = (match, isOutright) => (!isOutright ? match.matchId : match.id);

// Check for "suspended" status
const checkSuspendedStatus = (match, outcome, odd) => outcome.suspended
	|| match.suspended
	|| match.outrightSuspended
	|| (odd !== undefined && odd.suspended);

/**
 * Get item type
 * @returns {ITEM_TYPES[keyof ITEM_TYPES]}
 */
const getMatchItemType = (outcome, isOutright, isPrematch, isLive) => {
	switch (true) {
		case String(outcome.id).startsWith('p-o'):
			return ITEM_TYPES.PLAYER;
		case isOutright:
			return ITEM_TYPES.OUTRIGHT;
		case isPrematch:
			return ITEM_TYPES.PRE_MATCH;
		case isLive:
			return ITEM_TYPES.LIVE;
	}
	return outcome.itemType;
};

const getMarketInfo = (match, odd, outcome, itemType, isRace) => {
	if (itemType === ITEM_TYPES.PLAYER) {
		return {
			...odd,
			marketId: `${outcome.playerOddId}`,
		};
	}
	if (itemType === ITEM_TYPES.OUTRIGHT) {
		const marketName = isRace ? odd.marketName : match.name;
		return {
			marketName,
			marketId: match.outcomeId,
		};
	}
	return odd;
};

/**
 * Get basic info for betbuilder
 * @param {BetBuilderFixture} fixture - fixture
 * @param {number} marketId - betbuilder virutal market id
 * @param {number} outcomeId - betbuilder virtual outcome id
 */
export function mapBetbuilderFixture(fixture, marketId, outcomeId) {
	let odd = {
		marketName: '', // Empty string
		marketId,
	};
	let outcome = {
		name: fixture.matchName, // Match name,
		id: outcomeId, // Get if from settings
		oddTypeOutcomeId: outcomeId, // Get it from settings (outcomeID)
		originalOdd: '1', // When setting for first time odd is 1 after that we get it from API
		itemType: ITEM_TYPES.BET_BUILDER, // BetBuilder Item Type
	};
	// Get this from fixture
	let match = {
		sportCastFixtureId: fixture.id, // add it to match object as well
		sportCastSportId: fixture.sportCastSportId,
		swishFixtureId: fixture.swishFixtureId,
		matchId: fixture.matchId,
		name: fixture.matchName, // Empty string
		sportId: fixture.sportId,
		categoryId: fixture.categoryId,
		tournamentId: fixture.tournamentId,
		eventCode: fixture.eventCode,
		tournamentName: fixture.tournamentName,
		categoryName: fixture.categoryName,
	};
	const betslipItem = mapMatchToBetslipItem(match, outcome, odd);
	const betslipMatch = mapMatchToBetslipMatchItem(match);
	if (fixture.swishFixtureId) {
		betslipMatch.swMatchId = fixture.swMatchId;
		betslipMatch.swSwishMatchID = fixture.swSwishMatchID;
		betslipMatch.swTournamentName = fixture.swTournamentName;
		betslipMatch.swHomeTeamID = fixture.swHomeTeamID;
		betslipMatch.swAwayTeamID = fixture.swAwayTeamID;
	}
	return { betslipItem, betslipMatch };
}

export const mapMatchToBetslipItem = (match, outcome, odd, isOutright, isPrematch, isRace, isLive) => {
	const itemType = getMatchItemType(outcome, isOutright, isPrematch, isLive);
	const market = getMarketInfo(match, odd, outcome, itemType, isRace);
	/**
	 * @type {BetslipItem}
	 */
	let betslip_item = {
		marketName: market.enrichedMarketName || market.marketName,
		marketId: market.marketId,
		matchId: getMatchId(match, isOutright),
		itemType,
		outcomeName: outcome.enrichedOutcomeName || outcome.name,
		outcomeId: outcome.id,
		oddTypeOutcomeId: outcome.oddTypeOutcomeId,
		odd: outcome.originalOdd || outcome.originalDecimalOutcome,
		sbv: outcome.sbv,
		identifier: outcome.identifier,
		specifier: outcome.specifier,
	};

	// TODO: refactor player mappings so we don't have to map it here
	if (`${outcome.id}`.startsWith('p-o')) {
		let exploded = outcome.id.split('-');
		betslip_item.outcomeId = +exploded[4];
		betslip_item.marketId = +exploded[5];
		betslip_item.marketName = betslip_item.outcomeName;
		betslip_item.outcomeName = odd.playerName || odd.marketName;
		betslip_item.playerOddTypeId = odd.competitorId;
		betslip_item.sbv = outcome.name;
	}
	if (outcome.isRaceCast) {
		betslip_item.competitorGroupId = outcome.id
		betslip_item.outcomeName = match.name
		betslip_item.marketName = odd.marketName
		let runnersDetails = outcome.name.split(',')
		betslip_item.runnersDetails = runnersDetails
	}
	if (isOutright) {
		betslip_item.playerOddTypeId = outcome.cmid || outcome.competitorId;
	}

	if (checkSuspendedStatus(outcome, outcome, odd)) {
		betslip_item.isLocked = true;
	}
	if (outcome.upDownClass) {
		betslip_item.upDown = outcome.upDownClass;
	}
	if (market.cashout) {
		betslip_item.hasCashout = true;
	}

	if (itemType === ITEM_TYPES.BET_BUILDER) {
		betslip_item.betBuilder = {};
	}

	if (isRace && match.outcomes && match.outcomes[1]) {
		betslip_item.raceOdd = {
			id: match.outcomes[1].id,
			marketId: match.outcomes[1].outcomeId,
			label: match.ewplabel,
			place: match.eachWayPlace,
			odd: match.outcomes[1].originalOdd || match.outcomes[1].outcome,
		};
	}

	return betslip_item;
}

export const mapMatchToBetslipMatchItem = (match, isOutright, isRace) => {
	/**
	 * @type {BetslipMatchItem}
	 */
	let betslipMatchItem = {
		matchId: getMatchId(match, isOutright),
		matchName: match.name,
		homeTeam: match.homeTeam,
		awayTeam: match.guestTeam,
		sportId: match.sportId,
		categoryId: match.categoryId,
		tournamentId: match.tournamentId,
		categoryName: match.categoryName,
		tournamentName: match.tournamentName,
		eventCode: match.eventCode,
		betRadarId: match.betRadarId,
	}
	if (match.sportCastFixtureId) {
		betslipMatchItem.sportCastFixtureId = match.sportCastFixtureId;
		betslipMatchItem.sportCastSportId = match.sportCastSportId;
	}
	if (match.swishFixtureId) {
		betslipMatchItem.swishFixtureId = match.swishFixtureId;
	}
	if (isOutright) {
		betslipMatchItem.matchName = match.endDate;
	}
	if (isRace) {
		betslipMatchItem.matchName = `${match.date} ${match.time} ${match.raceName}`;
	}
	return betslipMatchItem;
};

/**
 * Get set betslip layout
 */
export const getLayout = (itemsCount = 2, freebetId = 0) => {
	if (freebetId) {
		return BETSLIP_LAYOUTS.TABS;
	}
	if (itemsCount <= 1) {
		return BETSLIP_LAYOUTS.TABS;
	}
	/**
	 * @type {keyof BETSLIP_LAYOUTS}
	 */
	let keyName = 'TABS';
	if (APP_SETTINGS && APP_SETTINGS.betslip_v2 && APP_SETTINGS.betslip_v2.layout) {
		keyName = APP_SETTINGS.betslip_v2.layout.toUpperCase();
	}
	return BETSLIP_LAYOUTS[keyName] || BETSLIP_LAYOUTS.TABS;
};


/**
 * Check if given tab is enabled in global settings 
 * @param {string} tabName - tab name
 */
function checkTab(tabName) {
	return APP_SETTINGS.betslip_v2
		&& APP_SETTINGS.betslip_v2.tabs
		&& APP_SETTINGS.betslip_v2.tabs.toLowerCase().includes(tabName);
}

/**
 * Check if algo tab is enabled in global settings 
 * @param {BetslipStore} betslipStore - betslip store
 */
function checkAlgoTab(betslipStore) {
	if (!APP_SETTINGS.betslip_v2.algoTabMode){
		return true;
	}
	return !Object.values(betslipStore.algoItems).some(item => item.singles.length > 1);
}

/**
 * Get selected tab in betslip
 * @param {BetslipStore} betslipStore - betslip store
 */
export function getSelectedTab(betslipStore) {
	// If freebet is selected return combi tab
	if (betslipStore.freebetId) {
		return BETSLIP_TYPES.COMBI;
	}
	const itemsCount = Object.keys(betslipStore.items).length;
	// If we have single item return single tab

	if (itemsCount <= 1 && checkAlgoTab(betslipStore)) {
		return BETSLIP_TYPES.SINGLE;
	}
	const maxFoldCount = Object.keys(betslipStore.matchItems).length;
	const combiLines = betslipStore.foldLines[maxFoldCount] || 0;
	// If combi is selected and we have incompatible selections change the selected tab
	if (betslipStore.selectedTab === BETSLIP_TYPES.COMBI && combiLines > 1) {
		if (checkTab('system')) {
			return BETSLIP_TYPES.SYSTEM;
		}
		if (checkTab('single')) {
			return BETSLIP_TYPES.SINGLE;
		}
	}

	return betslipStore.selectedTab;
}

/**
 * Get assisted bet options based on global setting 
 */
export function getAssitedBetOptions() {
	if (!APP_SETTINGS.assistedBet || !APP_SETTINGS.assistedBet.searchUserOptions) {
		return Object.values(ASSISTED_BET_OPTIONS);
	}
	/**
	 * @type {Array<keyof ASSISTED_BET_OPTIONS>}
	 */
	const optionKeys = APP_SETTINGS.assistedBet.searchUserOptions.split(',').map(o => o.trim());
	return optionKeys.filter(o => ASSISTED_BET_OPTIONS[o]).map(o => ASSISTED_BET_OPTIONS[o]);
}

/**
 * Get class for quickbet in match odd
 * @param {import('bto-models/toast-notification/interfaces').ToastNotification} [qbNotification]
 * @returns {PLACE_STATUS_TO_QUICKBET_CLASS[keyof PLACE_STATUS_TO_QUICKBET_CLASS]|""} - mapped class or empty string
 */
export function getQuickBetClass(qbNotification) {
	if (!qbNotification) {
		return '';
	}
	if (qbNotification.betslip.status === BETSLIP_MSG_STATUS.ITEMS_CHANGED
		&& qbNotification.betslip.response.ChangeMatchesOdds.length === 0) {
		return PLACE_STATUS_TO_QUICKBET_CLASS[BETSLIP_MSG_STATUS.ERROR];
	}

	return PLACE_STATUS_TO_QUICKBET_CLASS[qbNotification.betslip.status] || '';
}

/**
 * Get betBuilder object from mybet item
 * @param {string} marketsInfo - betbuilder market info in string format separated by comma -> labelId|marketName|marketGlobalLongId
 * @param {string} marketsDescription - betbuilder market description separated by comma 
 * @param {{sfm: Pick<import('./interfaces').ApiBetBuilderRebet, 'sfm'>, [x:number]: import('./interfaces').ApiBetBuilderRebetLd}} bbd
 * @returns {{[globalId: string]: import('./interfaces').BetslipBetBuilderMarket}}
 */
export function getBetBuilderItemsFromRebet(marketsInfo, marketsDescription, bbd = null) {
	const betBuilderItems = marketsInfo.split(",");
	const descriptions = marketsDescription.split(",");

	return betBuilderItems.reduce((acc, bbItem, idx) => {
		const bbItemDetails = bbItem.split("|");
		const isSwish = !!bbd.sfm;
		const marketName = isSwish ? descriptions[idx].split(" - ")[0] : bbItemDetails[1];
		const globalId = isSwish ? bbItemDetails[1] : bbItemDetails[2];
		acc[globalId] = {
			description: descriptions[idx] || '',
			name: marketName,
			globalId,
			// labelId,
		};
		if (!isSwish) {
			acc[globalId].labelId = +bbItemDetails[0];
		}
		if (isSwish) {
			const marketKey = bbd[idx].mpid ? 'mp' : 'mm';
			const oddKey = bbd[idx].lt === "o" ? 'oo' : 'uo';
			const descParts = (descriptions[idx] || '').split(' - ');
			acc[globalId].description = descParts[1] || descParts[0] || '';
			acc[globalId].isComplex = false;
			acc[globalId].matchId = bbd[idx].mid;
			acc[globalId].statsId = bbd[idx][marketKey].sid;
			acc[globalId].line = bbd[idx][marketKey].l;
			acc[globalId].odd = bbd[idx][marketKey][oddKey];
			if (bbd[idx].mpid) {
				acc[globalId].matchPlayerId = bbd[idx].mpid;
				acc[globalId].playerId = bbd[idx].mp.sp;
			}
			if (bbd[idx].mmid) {
				acc[globalId].preMatchMarketId = bbd[idx].mmid;
				acc[globalId].team = bbd[idx].mm.t;
			}
		}
		return acc;
	}, {});
}

/**
 * Add betbuilder info into betslip item and match item during rebet if item is betbuilder 
 * @param {BetslipItem} betslipItem 
 * @param {BetslipMatchItem} betslipMatchItem 
 * @param {ITEM_TYPES[keyof ITEM_TYPES]} itemType 
 * @param {{[matchId:number]:import('./interfaces').ApiBetBuilderRebet}} betBuilderMybetDetails 
 */
export function remapBetBuilderRebet(betslipItem, betslipMatchItem, itemType, betBuilderMybetDetails) {
	if (itemType !== ITEM_TYPES.BET_BUILDER) {
		return;
	}
	const itemDetails = betBuilderMybetDetails[betslipItem.matchId];
	const betslipItemBetBuilder = getBetBuilderItemsFromRebet(itemDetails.bbids, betslipItem.marketName, { ...itemDetails.ld, sfm: itemDetails.sfm, });
	if (itemDetails.scfi && itemDetails.scsid) {
		betslipMatchItem.sportCastFixtureId = itemDetails.scfi;
		betslipMatchItem.sportCastSportId = itemDetails.scsid;
	}
	if (itemDetails.sfm) {
		betslipMatchItem.swishFixtureId = itemDetails.sfm.sm;
		betslipMatchItem.swAwayTeamID = itemDetails.sfm.a;
		betslipMatchItem.swHomeTeamID = itemDetails.sfm.h;
		betslipMatchItem.swMatchId = itemDetails.sfm.m
		betslipMatchItem.swSwishMatchID = itemDetails.sfm.sm;
		betslipMatchItem.swTournamentName = itemDetails.sfm.t;
	}
	betslipItem.betBuilder = betslipItemBetBuilder;
	betslipItem.marketName = "";
	betslipItem.outcomeName = betslipMatchItem.matchName.replace(" () - ", "");
	betslipItem.oddTypeOutcomeId = betslipItem.outcomeId;
	betslipItem.itemType = ITEM_TYPES.BET_BUILDER;
}

/**
 * Map boosted item to betslip item/match
 * @param {import('../boosted-odd/boosted-odd-item.model').default} boostedItem - Boosted Item
 * @param {string} boostedTranslation - translated key for BO.BoostedOdd
 * @returns {{item: BetslipItem, match: BetslipMatchItem}}
 */
export function mapBoostedBetToBetslip(boostedItem, boostedTranslation) {
	/**
	 * @type {BetslipItem}
	 */
	const item = {
		itemType: ITEM_TYPES.BOOSTED_ODD,
		marketId: 0,
		outcomeId: 0,
		marketName: boostedItem.description,
		matchId: boostedItem.id,
		eventIds: boostedItem.eventIds,
		odd: boostedItem.currentOdd,
		outcomeName: boostedTranslation,
	};
	/**
	 * @type {BetslipMatchItem}
	 */
	const match = {
		eventCode: 0,
		matchId: boostedItem.id,
		eventIds: boostedItem.eventIds,
		matchName: boostedItem.matchName,
		sportId: boostedItem.sportId,
		sportName: boostedItem.sport,
		tournamentId: boostedItem.tournamentTemplateId,
		tournamentName: boostedItem.tournamentName,
		totalBetItems: 0,
		categoryId: boostedItem.categoryId,
		categoryName: boostedItem.categoryName,
	};
	return {
		item,
		match,
	}
}

/**
 * Get betslip item event route name
 * @param {BetslipMatchItem} betMatchItem - betslip item
 */
function getItemEventRouteName(betMatchItem) {
	const eventName = urlFriendlyParam(betMatchItem.matchName);
	if (eventName.includes('-@-')) {
		const [awayTeam, homeTeam] = eventName.split('-@-');
		return `${homeTeam}-vs-${awayTeam}`;
	}
	return eventName;
}

/**
 * Get betslip item route object or undefined
 * @param {BetslipItem} item - betslip item
 * @param {BetslipMatchItem} betMatchItem - betslip match item
 * @param {string} rawSportName - sport name
 */
export function getBetslipItemRoute(item, betMatchItem, rawSportName) {
	const sportName = urlFriendlyParam(rawSportName);
	const categoryName = urlFriendlyParam(betMatchItem.tournamentName);
	const eventName = getItemEventRouteName(betMatchItem);
	const fixtureIds = `${betMatchItem.tournamentId || '-'}`;
	const categoryId = `${betMatchItem.categoryId || '-'}`

	if (item.raceOdd && process.env.VUE_APP_IS_MOBILE) {
		return {
			name: 'seoSport',
			params: { sportName, sportId: betMatchItem.sportId }
		}
	}

	if (item.raceOdd || item.runnersDetails) {
		return {
			name: 'raceDetailsHr',
			params: { raceDateFilter: 100, sportId: betMatchItem.sportId, raceId: betMatchItem.rTournamentId },
		};
	}

	if (item.itemType === ITEM_TYPES.OUTRIGHT) {
		return {
			name: 'seoCategory',
			params: { sport:sportName, category:categoryName, categoryId },
		}
	}

	if (item.betBuilder) {
		return {
			name: 'seoGame',
			params: { sportName, categoryName, eventName, matchId: betMatchItem.matchId, gameId: betMatchItem.matchId },
			query: {
				scfi: betMatchItem.sportCastFixtureId,
				sm: betMatchItem.swishFixtureId,
			}
		};

	}
	if (item.itemType === ITEM_TYPES.PRE_MATCH || item.itemType === ITEM_TYPES.PLAYER) {
		return {
			name: 'seoGame',
			params: { sportName, categoryName, eventName, matchId: betMatchItem.matchId, gameId: betMatchItem.matchId }
		};
	}
	if (item.itemType === ITEM_TYPES.LIVE) {
		return ({ name: 'seoGameLive', params: { sportName, categoryName, eventName, matchId: betMatchItem.betRadarId, gameId: betMatchItem.betRadarId } });
	}
}

/**
 * Map race cast odds into betslip item and match item
 * @param {import('bto-models/match/races/interfaces').RaceCastOdd} race - race cast odd
 * @param {any} raceOdds - race details model
 * @param {number} sportId - sport id
 * @param {import('bto-models/match/races/interfaces').RaceCastRunnerDetails[]} runnersDetails 
 * @returns 
 */
export function mapCastToBetslip(race, raceOdds, sportId, runnersDetails) {
	/**
	 * @type {BetslipItem}
	 */
	let marketName = Vue.prototype.$translate.text(RACE_MARKET_ID_NAME[race.oddTypeID])
	let betslip_item = {
		// TODO: translate market name
		marketName: marketName,
		marketId: race.oddTypeID,
		matchId: raceOdds.outrightID || raceOdds.outrightId,
		itemType: ITEM_TYPES.OUTRIGHT,
		outcomeName: raceOdds.raceName,
		outcomeId: race.otoid,
		oddTypeOutcomeId: race.otoid,
		odd: `${race.oo}`,
		competitorGroupId: race.potid,
		competitorId: race.cmid,
		playerOddTypeId: race.potid,
		runnersDetails: runnersDetails
	};

	/**
	 * @type {BetslipMatchItem}
	 */
	let betslipMatchItem = {
		matchId: raceOdds.outrightID || raceOdds.outrightId,
		matchName: raceOdds.date || raceOdds.apiDateTime,
		sportId: sportId || 31,
		eventCode: raceOdds.outrightID || raceOdds.outrightId,
		rTournamentId: `hr${raceOdds.tournamentId}` || ''
	}

	return { betslip_item, betslipMatchItem };
}